import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../login/auth.css';
import '../signup/signup.css';
import Message from '../../components/ui/Message';
import { InstanceLogo } from '../../components/ui/InstanceLogo';
import { BRAND_NAME } from '../../utils/appConfig';
import { Canton, District, Province } from '../../models/interfaces';
import MccSelect from './MccSelect';
import { RegisterContext } from '../../context/register/RegisterContext';
import { mccCodes } from './mccCodes';
import imageCompression from "browser-image-compression";
import { useRequiredFields } from '../../hooks/useRequiredFields';



type BankAccount = {
  iban: string;
  currency: 'CRC' | 'USD';
  name: string;
};

const STEPS = [
  { stepNumber: 1, label: "Productos", iconClass: "bi-grid", title: "Seleccione sus productos", description: "Elija los productos que necesita y revise los requisitos antes de empezar." },
  { stepNumber: 2, label: "Personales", iconClass: "bi-person", title: "Datos personales", description: "Datos del solicitante y del representante legal." },
  { stepNumber: 3, label: "Comercio", iconClass: "bi-shop", title: "Datos del comercio", description: "Información, dirección y servicios del comercio." },
  { stepNumber: 4, label: "Bancarios", iconClass: "bi-bank", title: "Datos bancarios", description: "Cuenta donde recibirá los depósitos." },
  { stepNumber: 5, label: "Documentos", iconClass: "bi-files", title: "Documentos", description: "Adjunte los documentos según su tipo de afiliación." },
];

const RegisterPage = () => {

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string>("");

  const required = useRequiredFields();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [step, setStep] = useState(1);

  const [nombre, setNombre] = useState<string>("");
  const [primerApellido, setPrimerApellido] = useState<string>("");
  const [segundoApellido, setSegundoApellido] = useState<string>("");
  const [correo, setCorreo] = useState<string>("");
  const [telefono, setTelefono] = useState<string>("");
  const [tipoAfiliacion, setTipoAfiliacion] = useState<string>(""); // 'fisica' o 'juridica'

  // Campos adicionales para Persona Jurídica
  const [cedulaJuridica, setCedulaJuridica] = useState<string>("");
  const [razonSocial, setRazonSocial] = useState<string>("");
  const [tipoCedulaRep, setTipoCedulaRep] = useState<string>(""); // 'cedula' o 'dimex'
  const [numeroCedulaRep, setNumeroCedulaRep] = useState<string>("");

  // Step 3
  const [commerceName, setCommerceName] = useState('');
  const [commerceCurrency, setCommerceCurrency] = useState('CRC');
  const [mcc, setMcc] = useState('');
  const [address, setAddress] = useState('');
  const [district, setDistrict] = useState('');
  const [canton, setCanton] = useState('');
  const [province, setProvince] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);

  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cantons, setCantons] = useState<Canton[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [inscritaHacienda, setInscritaHacienda] = useState(false);
  const [simplifiedRegime, setSimplifiedRegime] = useState(false);
  const [webPage, setWebPage] = useState('');


  // Step 4
  const [mainAccount, setMainAccount] = useState<BankAccount>({ iban: '', currency: 'CRC', name: '' });
  const [referralSource, setReferralSource] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  // Step 5
  const [cedulas, setCedulas] = useState([]);
  const [personeria, setPersoneria] = useState(null);
  const [cedulaFrente, setCedulaFrente] = useState(null);
  const [cedulaDetras, setCedulaDetras] = useState(null);
  const [fotosLocal, setFotosLocal] = useState([null, null, null, null, null]);
  const [fotosLocalCount, setFotosLocalCount] = useState(1);

  // Productos que se ofrecen en la afiliación PayFac, con su esquema de costos
  const usagePricing = { price: '$1', unit: '/día de uso', legend: '$1 por día de uso · 0.50% por transferencia a cualquier banco · 4.16% por transacción. Sin más costos.' };
  const availableProducts = [
    { name: 'POS', icon: 'bi-calculator', description: 'Punto de venta para cobrar en su comercio.',
      price: '$10', unit: '/mes', legend: '$10 mensual · $0.50 por transferencia a cualquier banco · 4.16% por transacción. Sin más costos.' },
    { name: 'SwiftClick', icon: 'bi-link-45deg', description: 'Links de pago para cobrar a distancia.', ...usagePricing },
    { name: 'SwiftBotón', icon: 'bi-hand-index', description: 'Botón de pago para su sitio web.', ...usagePricing },
  ];


  const { loadProvinces, loadCantons, loadDistricts, isLoading, save } = useContext(RegisterContext);

  useEffect(() => {
    loadProvinces().then((resp) => {
      if (resp) {
        setProvinces(resp.provinces);
      }
    })
  }, []);

  useEffect(() => {
    if (province == '') return;
    loadCantons(parseInt(province)).then((resp) => {
      if (resp) {
        setCantons(resp.cantons);
      }
    })
  }, [province]);

  useEffect(() => {
    if (canton == '') return;
    loadDistricts(parseInt(province), parseInt(canton)).then((resp) => {
      if (resp) {
        setDistricts(resp.districts);
      }
    })
  }, [canton]);

  const getProvinceName = (code: number | null) =>
    provinces.find(p => p.provinceCode === code)?.provinceName || '';

  const getCantonName = (code: number | null) =>
    cantons.find(c => c.cantonCode === code)?.cantonName || '';

  const getDistrictName = (code: number | null) =>
    districts.find(d => d.districtCode === code)?.districtName || '';

  const handleFotoChange = (index, file) => {
    const updatedFotos = [...fotosLocal];
    updatedFotos[index] = file;
    setFotosLocal(updatedFotos);
  };

  const saveForm = async () => {
    let canProceed = false;



    canProceed =
      cedulaFrente != null &&
      cedulaDetras != null &&
      (
        (tipoAfiliacion === "fisica" && fotosLocal[0] != null) ||
        (tipoAfiliacion === "juridica" && personeria != null && fotosLocal[0] != null)
      ) &&
      acceptedTerms &&
      acceptedPrivacy;


    if (!canProceed) {
      required.fail();
      setErrorMessage("Por favor complete todos los campos obligatorios antes de continuar.");
      return;
    }

    if (cedulas.length > 6) {
      setErrorMessage("La cantidad de cédulas adjuntas no debe ser mayor a 6.");
      return;
    }

    if (fotosLocal.length > 5) {
      setErrorMessage("La cantidad de fotos del local adjuntas no debe ser mayor a 5.");
      return;
    }



    /*const request = {
      name: nombre,
      firstLastName: primerApellido,
      secondLastName: segundoApellido,
      contactEmail: correo,
      phone: telefono,
      affiliationType: tipoAfiliacion,
      legalId: cedulaJuridica,
      repIdType: tipoCedulaRep,
      repIdNumber: numeroCedulaRep,
      commerceName,
      commerceCurrency,
      industry,
      address,
      district: getDistrictName(parseInt(district)),
      canton: getCantonName(parseInt(canton)),
      province: getProvinceName(parseInt(province)),
      platforms: platforms.join(","),
      registeredTax: inscritaHacienda ? 'Si' : 'No',
      simplifiedRegime: simplifiedRegime ? 'Si' : 'No',
      ibanAccount: mainAccount.iban,
      currencyAccount: mainAccount.currency,
      nameAccount: mainAccount.name,
      referralSource,
      cedulas,
      personeria,
      fotosLocal
    }*/

    const formData = new FormData();

    formData.append("name", nombre);
    formData.append("firstLastName", primerApellido);
    formData.append("secondLastName", segundoApellido);
    formData.append("contactEmail", correo);
    formData.append("phone", telefono);
    formData.append("affiliationType", tipoAfiliacion == "fisica" ? "Física" : "Jurídica");
    formData.append("legalId", cedulaJuridica);
    formData.append("repIdType", tipoCedulaRep == 'cedula' ? 'Cédula' : 'DIMEX');
    formData.append("repIdNumber", numeroCedulaRep);
    formData.append("commerceName", commerceName.toUpperCase());
    formData.append("commerceCurrency", commerceCurrency);
    const selectedMcc = mccCodes.find(m => m.code === mcc);
    formData.append("industry", selectedMcc ? `${selectedMcc.code} - ${selectedMcc.description}` : mcc);
    formData.append("mcc", mcc);
    formData.append("mccDescription", selectedMcc?.description ?? "");
    formData.append("businessName", tipoAfiliacion === "juridica" ? razonSocial.trim() : "");
    formData.append("address", address);
    formData.append("district", getDistrictName(parseInt(district)));
    formData.append("canton", getCantonName(parseInt(canton)));
    formData.append("province", getProvinceName(parseInt(province)));
    formData.append("platforms", platforms.join(","));
    formData.append("registeredTax", inscritaHacienda ? "Si" : "No");
    formData.append("simplifiedRegime", simplifiedRegime ? "Si" : "No");
    formData.append("ibanAccount", mainAccount.iban);
    formData.append("currencyAccount", mainAccount.currency);
    formData.append("nameAccount", mainAccount.name);
    formData.append("referralSource", referralSource);
    formData.append("webPage", webPage);

    // Archivos múltiples: cedulas

    // Cédulas
    if (cedulaFrente) {
      formData.append("CedulaFrente", cedulaFrente);
    }

    if (cedulaDetras) {
      formData.append("CedulaDetras", cedulaDetras);
    }

    // Personería (solo si aplica)
    if (personeria) {
      formData.append("Personeria", personeria);
    }

    // Fotos del local (solo las no nulas)
    fotosLocal.forEach((file, index) => {
      if (file) {
        formData.append(`Local${index + 1}`, file);
      }
    });

    const message = await save(formData);

    if (message == 'success') {
      setSuccessMessage("La información se guardó con éxito. Pronto será contactado por nuestro equipo de afiliación.")
    } else {
      const newMessage = (message == "Ya existe un registro para la empresa." ? message : 'Ocurrió un error guardando el formulario, por favor intente de nuevo.');
      setErrorMessage(newMessage);
    }


  };


  const nextStep = () => {
    let canProceed = false;

    switch (step) {
      case 1:
        // Paso 1: al menos un producto
        if (platforms.length === 0) {
          required.fail();
          setErrorMessage("Seleccione al menos un producto.");
          return;
        }
        canProceed = true;
        break;

      case 2:
        // Paso 2: Datos Personales

        let isNumeroCedulaRepValid = false;

        if (tipoCedulaRep === "cedula") {
          // Formato: #-####-#### (ej: 1-2345-6789)
          const regexCedula = /^\d-\d{4}-\d{4}$/;
          isNumeroCedulaRepValid = regexCedula.test(numeroCedulaRep.trim());
        } else {
          // Formato: 1########### (ej: 112345678901)
          const regexOtro = /^1\d{11}$/;
          isNumeroCedulaRepValid = regexOtro.test(numeroCedulaRep.trim());
        }

        if (!isNumeroCedulaRepValid) {
          required.fail();
          setErrorMessage("El formato del número de cédula del representante legal es inválido.");
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(correo)) {
          required.fail();
          setErrorMessage("El formato del correo es inválido.");
          return;
        }

        canProceed =
          nombre.trim() !== "" &&
          primerApellido.trim() !== "" &&
          correo.trim() !== "" &&
          telefono.trim() !== "" &&
          tipoAfiliacion.trim() !== "" &&
          (
            (tipoAfiliacion === "fisica" &&
              tipoCedulaRep.trim() !== "" &&
              numeroCedulaRep.trim() !== "") ||
            (tipoAfiliacion === "juridica" &&
              cedulaJuridica.trim() !== "" &&
              razonSocial.trim() !== "" &&
              tipoCedulaRep.trim() !== "" &&
              numeroCedulaRep.trim() !== "")
          );

        break;

      case 3:
        // Paso 3: Datos del Comercio (ejemplo, reemplazar por tus campos)
        canProceed = commerceName.trim() !== "" &&
          mcc.trim() !== "" &&
          commerceCurrency.trim() !== "" &&
          province.trim() !== "" &&
          canton.trim() !== "" &&
          district.trim() !== "" &&
          address.trim() !== "";
        break;

      case 4:
        // Paso 4: Datos Bancarios (ejemplo, reemplazar por tus campos)

        if (!isValidCostaRicaIBAN(mainAccount.iban)) {
          required.fail();
          setErrorMessage("La cuenta IBAN no es válida.");
          return;
        }
        canProceed = mainAccount.iban.trim() !== "" &&
          mainAccount.name.trim() !== "";
        break;
    }

    if (!canProceed) {
      required.fail();
      setErrorMessage("Por favor complete todos los campos obligatorios antes de continuar.");
      return;
    }

    setStep((prev) => Math.min(prev + 1, 5));
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const toLogin = (e: any) => {
    navigate('/login', {
      replace: true
    });
  }

  const removeError = (e: any) => {
    setErrorMessage('')
  }


  const removeSuccessMessage = (e: any) => {
    setSuccessMessage('')
    navigate('/login', {
      replace: true
    });
  }

  const getExtension = (mime: string): string => {
    switch (mime) {
      case "image/jpeg":
        return "jpg";
      case "image/png":
        return "png";
      case "image/webp":
        return "webp";
      default:
        return "jpg";
    }
  };

  /**
   * Comprime una imagen a máximo ~1MB y preserva extensión
   */
  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      initialQuality: 0.8
    };

    try {
      const compressedBlob = await imageCompression(file, options);

      // Obtener extensión correcta según MIME
      const extension = getExtension(compressedBlob.type);

      // Obtener nombre base sin extensión
      const originalName = file.name.replace(/\.[^/.]+$/, "");

      // Construir nuevo nombre con extensión correcta
      const newFileName = `${originalName}.${extension}`;

      const compressedFile = new File(
        [compressedBlob],
        newFileName,
        {
          type: compressedBlob.type,
          lastModified: Date.now()
        }
      );

      return compressedFile;

    } catch (error) {
      console.error("Error comprimiendo imagen:", error);
      return file;
    }
  };


  const processFile = async (file: File | null): Promise<File | null> => {
    if (!file) return null;

    if (file.type.startsWith("image/")) {
      return await compressImage(file);
    }

    return file; // PDFs no se comprimen aquí
  };


  const isValidCostaRicaIBAN = (iban: string): boolean => {
    // Normalizar: quitar espacios y mayúsculas
    iban = iban.replace(/\s+/g, '').toUpperCase();

    // Validar longitud y prefijo
    if (iban.length !== 22 || !iban.startsWith("CR")) {
      return false;
    }

    // Reordenar (mover los 4 primeros al final)
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    // Reemplazar letras por números (A=10, B=11, ..., Z=35)
    const numeric = rearranged.split('').map(ch => {
      if (/[A-Z]/.test(ch)) {
        return (ch.charCodeAt(0) - 55).toString(); // A=10
      }
      return ch;
    }).join('');

    // Verificar con módulo 97 en bloques
    let remainder = '';
    for (let i = 0; i < numeric.length; i += 9) {
      const part = remainder + numeric.substring(i, i + 9);
      remainder = (parseInt(part, 10) % 97).toString();
    }

    return parseInt(remainder, 10) % 97 === 1;
  }

  const togglePlatform = (platform: string) => {
    setPlatforms(prev => prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]);
  };


  // Al cambiar de paso se vuelve al inicio del formulario
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    required.reset();
  }, [step, required.reset]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 5) {
      nextStep();
    } else {
      saveForm();
    }
  };

  const currentStep = STEPS[step - 1];

  // Mismas reglas que nextStep, para marcar los campos en rojo
  const repIdValid = tipoCedulaRep === "cedula"
    ? /^\d-\d{4}-\d{4}$/.test(numeroCedulaRep.trim())
    : /^1\d{11}$/.test(numeroCedulaRep.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo);

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 text-muted">Procesando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <main className="auth-main">
        <div className="auth-card auth-card-xl">
          <InstanceLogo className="auth-logo" />

          {/* PASOS */}
          <ol className="auth-steps mb-4">
            {STEPS.map(({ stepNumber, label, iconClass }) => (
              <li
                key={stepNumber}
                className={step === stepNumber ? 'is-active' : step > stepNumber ? 'is-done' : ''}
                aria-current={step === stepNumber ? 'step' : undefined}
              >
                <span className="auth-step-dot">
                  <i className={`bi ${step > stepNumber ? 'bi-check-lg' : iconClass}`} />
                </span>
                <span className="auth-step-label">{label}</span>
              </li>
            ))}
          </ol>

          <div className="text-center mb-4">
            <div className="small text-muted text-uppercase fw-semibold mb-1">Paso {step} de {STEPS.length}</div>
            <h4 className="mb-1">{currentStep.title}</h4>
            <p className="text-muted mb-0">{currentStep.description}</p>
          </div>

          <form onSubmit={handleSubmit} noValidate key={step}>

            {/* PASO 1: PRODUCTOS Y REQUISITOS */}
            {step === 1 && (
              <>
                <div className={`small mb-2 ${required.attempted && platforms.length === 0 ? 'text-danger' : 'text-muted'}`}>
                  {required.attempted && platforms.length === 0 && <i className="bi bi-exclamation-circle me-1" />}
                  Seleccione al menos un producto.
                </div>
                <div className="signup-features">
                  {availableProducts.map(product => {
                    const on = platforms.includes(product.name);
                    return (
                      <button
                        key={product.name}
                        type="button"
                        className={`signup-feature ${on ? 'selected' : ''}`}
                        onClick={() => togglePlatform(product.name)}
                        aria-pressed={on}
                      >
                        <span className="signup-feature-icon"><i className={`bi ${product.icon}`} /></span>
                        <span className="signup-feature-body">
                          <span className="signup-feature-name">{product.name}</span>
                          <span className="signup-feature-desc">{product.description}</span>
                          <span className="signup-feature-desc">{product.legend}</span>
                        </span>
                        <span className="signup-feature-price">
                          {product.price}<small>{product.unit}</small>
                        </span>
                        <i className={`bi ${on ? 'bi-check-circle-fill' : 'bi-circle'} signup-feature-check`} />
                      </button>
                    );
                  })}
                </div>

                <div className="auth-section-title">Requisitos</div>
                <p className="text-muted">
                  Para completar su afiliación necesitamos algunos datos y documentos.
                  Si los tiene a mano, podrá terminar el proceso en minutos y comenzar a vender sin demora.
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="auth-box h-100">
                      <div className="fw-semibold mb-2"><i className="bi bi-person me-2 text-primary" />Persona Física</div>
                      <ul className="auth-checklist">
                        <li><i className="bi bi-check2" />Copia de la cédula de identidad</li>
                        <li><i className="bi bi-check2" />Fotografías del local comercial</li>
                        <li><i className="bi bi-check2" />Completar el formulario</li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="auth-box h-100">
                      <div className="fw-semibold mb-2"><i className="bi bi-building me-2 text-primary" />Persona Jurídica</div>
                      <ul className="auth-checklist">
                        <li><i className="bi bi-check2" />Copia de la cédula de identidad de los representantes legales</li>
                        <li><i className="bi bi-check2" />Personería jurídica actualizada</li>
                        <li><i className="bi bi-check2" />Fotografías del local comercial</li>
                        <li><i className="bi bi-check2" />Completar el formulario</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="alert alert-light border small d-flex gap-2 mt-3 mb-0">
                  <i className="bi bi-info-circle text-primary" />
                  <span>Los documentos presentados deben estar emitidos con una vigencia mínima de 1 mes.</span>
                </div>
              </>
            )}

            {/* PASO 2: DATOS PERSONALES */}
            {step === 2 && (
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="nombre" className="form-label small text-muted mb-1">Nombre *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-person" /></span>
                    <input type="text" className={`form-control${required.mark(!nombre.trim())}`} id="nombre" placeholder="Ingrese su nombre"
                      autoComplete="given-name" autoFocus value={nombre} onChange={(e) => setNombre(e.target.value)} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="primerApellido" className="form-label small text-muted mb-1">Primer apellido *</label>
                  <input type="text" className={`form-control${required.mark(!primerApellido.trim())}`} id="primerApellido" placeholder="Ingrese su primer apellido"
                    autoComplete="family-name" value={primerApellido} onChange={(e) => setPrimerApellido(e.target.value)} />
                  <div className="invalid-feedback">Campo requerido</div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="segundoApellido" className="form-label small text-muted mb-1">Segundo apellido</label>
                  <input type="text" className="form-control" id="segundoApellido" placeholder="Ingrese su segundo apellido"
                    value={segundoApellido} onChange={(e) => setSegundoApellido(e.target.value)} />
                </div>

                <div className="col-md-6">
                  <label htmlFor="correo" className="form-label small text-muted mb-1">Correo electrónico *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-envelope" /></span>
                    <input type="email" className={`form-control${required.mark(!emailValid)}`} id="correo" placeholder="correo@dominio.com"
                      autoComplete="email" value={correo} onChange={(e) => setCorreo(e.target.value)} />
                    <div className="invalid-feedback">{correo.trim() ? 'Ingrese un correo válido' : 'Campo requerido'}</div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="telefono" className="form-label small text-muted mb-1">Teléfono *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-telephone" /></span>
                    <input type="tel" className={`form-control${required.mark(!telefono.trim())}`} id="telefono" placeholder="Ingrese su teléfono"
                      autoComplete="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="tipoAfiliacion" className="form-label small text-muted mb-1">Tipo de afiliación *</label>
                  <select className={`form-select${required.mark(!tipoAfiliacion)}`} id="tipoAfiliacion" value={tipoAfiliacion}
                    onChange={(e) => setTipoAfiliacion(e.target.value)}>
                    <option value="">Seleccione</option>
                    <option value="fisica">Persona Física</option>
                    <option value="juridica">Persona Jurídica</option>
                  </select>
                  <div className="invalid-feedback">Campo requerido</div>
                </div>

                {tipoAfiliacion === "juridica" && (
                  <div className="col-md-6">
                    <label htmlFor="cedulaJuridica" className="form-label small text-muted mb-1">Cédula jurídica *</label>
                    <div className="input-group has-validation">
                      <span className="input-group-text"><i className="bi bi-building" /></span>
                      <input type="text" className={`form-control${required.mark(!cedulaJuridica.trim())}`} id="cedulaJuridica" placeholder="3-###-######"
                        value={cedulaJuridica} onChange={(e) => setCedulaJuridica(e.target.value)} />
                      <div className="invalid-feedback">Campo requerido</div>
                    </div>
                    <div className="form-text">Formato: 3-###-######</div>
                  </div>
                )}

                {tipoAfiliacion === "juridica" && (
                  <div className="col-md-6">
                    <label htmlFor="razonSocial" className="form-label small text-muted mb-1">Razón social *</label>
                    <input type="text" className={`form-control${required.mark(!razonSocial.trim())}`} id="razonSocial" placeholder="Nombre legal de la sociedad"
                      maxLength={150} value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                )}

                <div className="col-12">
                  <div className="auth-section-title">Representante legal</div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="tipoCedulaRep" className="form-label small text-muted mb-1">Tipo de identificación *</label>
                  <select className={`form-select${required.mark(!tipoCedulaRep)}`} id="tipoCedulaRep" value={tipoCedulaRep}
                    onChange={(e) => setTipoCedulaRep(e.target.value)}>
                    <option value="">Seleccione</option>
                    <option value="cedula">Cédula</option>
                    <option value="dimex">Dimex</option>
                  </select>
                  <div className="invalid-feedback">Campo requerido</div>
                </div>

                <div className="col-md-6">
                  <label htmlFor="numeroCedulaRep" className="form-label small text-muted mb-1">Número de identificación *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-person-vcard" /></span>
                    <input type="text" className={`form-control${required.mark(!repIdValid)}`} id="numeroCedulaRep"
                      placeholder={tipoCedulaRep === "dimex" ? "1###########" : "#-####-####"}
                      value={numeroCedulaRep} onChange={(e) => setNumeroCedulaRep(e.target.value)} />
                    <div className="invalid-feedback">{numeroCedulaRep.trim() ? 'El formato no es válido' : 'Campo requerido'}</div>
                  </div>
                  {tipoCedulaRep !== "" && (
                    <div className="form-text">
                      {tipoCedulaRep === "cedula" ? "Formato: #-####-####" : "Formato: 1###########"}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PASO 3: DATOS DEL COMERCIO */}
            {step === 3 && (
              <>
                <div className="row g-3">
                  <div className="col-12">
                    <label htmlFor="commerceName" className="form-label small text-muted mb-1">Nombre de fantasía *</label>
                    <div className="input-group has-validation">
                      <span className="input-group-text"><i className="bi bi-shop" /></span>
                      <input type="text" className={`form-control${required.mark(!commerceName.trim())}`} id="commerceName" placeholder="Nombre con el que se conoce el comercio"
                        autoFocus value={commerceName} onChange={(e) => setCommerceName(e.target.value)} />
                      <div className="invalid-feedback">Campo requerido</div>
                    </div>
                  </div>

                  <div className="col-md-8">
                    <MccSelect
                      codes={mccCodes}
                      value={mcc}
                      onChange={setMcc}
                      label="Actividad comercial (MCC) *"
                      invalid={required.attempted && !mcc}
                    />
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="commerceCurrency" className="form-label small text-muted mb-1">Moneda principal *</label>
                    <select className="form-select" id="commerceCurrency" value={commerceCurrency}
                      onChange={(e) => setCommerceCurrency(e.target.value)}>
                      <option value="CRC">CRC</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="auth-option">
                      <input type="checkbox" className="form-check-input" id="inscritaHacienda"
                        checked={inscritaHacienda} onChange={() => setInscritaHacienda(!inscritaHacienda)} />
                      <span>
                        <span className="d-block fw-semibold">Actividad inscrita ante la Administración Tributaria de Costa Rica *</span>
                        <span className="d-block small text-muted">
                          Es requisito estar inscrito ante la Administración Tributaria y la actividad económica
                          debe coincidir con las referencias brindadas (documentos, nombre comercial, etc.); en caso contrario se rechazará la afiliación.
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="col-12">
                    <label className="auth-option">
                      <input type="checkbox" className="form-check-input" id="simplifiedRegime"
                        checked={simplifiedRegime} onChange={() => setSimplifiedRegime(!simplifiedRegime)} />
                      <span>
                        <span className="d-block fw-semibold">Actividad bajo régimen simplificado</span>
                        <span className="d-block small text-muted">
                          Solo marcar si la actividad bajo la cual se encuentra registrada la persona física o jurídica es bajo régimen simplificado.
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="col-12">
                    <div className="auth-section-title">Dirección del comercio</div>
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="province" className="form-label small text-muted mb-1">Provincia *</label>
                    <select className={`form-select${required.mark(!province)}`} id="province" value={province} onChange={(e) => setProvince(e.target.value)}>
                      <option value="">Seleccione</option>
                      {provinces.map((prov: Province) => (
                        <option key={prov.provinceCode} value={prov.provinceCode}>{prov.provinceName}</option>
                      ))}
                    </select>
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="canton" className="form-label small text-muted mb-1">Cantón *</label>
                    <select className={`form-select${required.mark(!canton)}`} id="canton" value={canton} disabled={province === ''}
                      onChange={(e) => setCanton(e.target.value)}>
                      <option value="">Seleccione</option>
                      {cantons.map((c: Canton) => (
                        <option key={c.cantonCode} value={c.cantonCode}>{c.cantonName}</option>
                      ))}
                    </select>
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>

                  <div className="col-md-4">
                    <label htmlFor="district" className="form-label small text-muted mb-1">Distrito *</label>
                    <select className={`form-select${required.mark(!district)}`} id="district" value={district} disabled={canton === ''}
                      onChange={(e) => setDistrict(e.target.value)}>
                      <option value="">Seleccione</option>
                      {districts.map((d: District) => (
                        <option key={d.districtCode} value={d.districtCode}>{d.districtName.toUpperCase()}</option>
                      ))}
                    </select>
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>

                  <div className="col-12">
                    <label htmlFor="address" className="form-label small text-muted mb-1">Dirección exacta *</label>
                    <div className="input-group has-validation">
                      <span className="input-group-text"><i className="bi bi-geo-alt" /></span>
                      <input type="text" className={`form-control${required.mark(!address.trim())}`} id="address" placeholder="Señas del comercio"
                        value={address} onChange={(e) => setAddress(e.target.value)} />
                      <div className="invalid-feedback">Campo requerido</div>
                    </div>
                  </div>

                  <div className="col-12">
                    <label htmlFor="webPage" className="form-label small text-muted mb-1">Página web</label>
                    <div className="input-group has-validation">
                      <span className="input-group-text"><i className="bi bi-globe" /></span>
                      <input type="text" className="form-control" id="webPage" placeholder="www.sucomercio.com"
                        value={webPage} onChange={(e) => setWebPage(e.target.value)} />
                    </div>
                  </div>

                </div>
              </>
            )}

            {/* PASO 4: DATOS BANCARIOS */}
            {step === 4 && (
              <div className="row g-3">
                <div className="col-md-8">
                  <label htmlFor="iban" className="form-label small text-muted mb-1">Número IBAN de la cuenta principal *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-bank" /></span>
                    <input type="text" className={`form-control${required.mark(!isValidCostaRicaIBAN(mainAccount.iban))}`} id="iban" maxLength={22} minLength={22}
                      placeholder="CR00000000000000000000" autoFocus value={mainAccount.iban}
                      onChange={(e) => setMainAccount({ ...mainAccount, iban: e.target.value })} />
                    <div className="invalid-feedback">{mainAccount.iban.trim() ? 'La cuenta IBAN no es válida' : 'Campo requerido'}</div>
                  </div>
                  <div className="form-text">22 caracteres, inicia con CR.</div>
                </div>

                <div className="col-md-4">
                  <label htmlFor="ibanCurrency" className="form-label small text-muted mb-1">Moneda de la cuenta *</label>
                  <select className="form-select" id="ibanCurrency" value={mainAccount.currency}
                    onChange={(e) => setMainAccount({ ...mainAccount, currency: e.target.value as 'CRC' | 'USD' })}>
                    <option value="CRC">CRC</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div className="col-12">
                  <label htmlFor="ibanName" className="form-label small text-muted mb-1">Nombre del dueño de la cuenta *</label>
                  <div className="input-group has-validation">
                    <span className="input-group-text"><i className="bi bi-person" /></span>
                    <input type="text" className={`form-control${required.mark(!mainAccount.name.trim())}`} id="ibanName" placeholder="Nombre del dueño de la cuenta IBAN"
                      value={mainAccount.name} onChange={(e) => setMainAccount({ ...mainAccount, name: e.target.value })} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                  <div className="form-text">El nombre del dueño debe ser exacto como aparece en el banco.</div>
                </div>
              </div>
            )}

            {/* PASO 5: DOCUMENTOS */}
            {step === 5 && (
              <>
                <div className="auth-section-title mt-0">Cédula de identidad *</div>
                <div className="row g-3 mb-1">
                  <div className="col-md-6">
                    <label htmlFor="cedulaFrente" className="form-label small text-muted mb-1">Frente *</label>
                    <input type="file" className={`form-control${required.mark(cedulaFrente == null)}`} id="cedulaFrente" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        const processed = await processFile(file);
                        setCedulaFrente(processed);
                      }} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="cedulaDetras" className="form-label small text-muted mb-1">Reverso *</label>
                    <input type="file" className={`form-control${required.mark(cedulaDetras == null)}`} id="cedulaDetras" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        const processed = await processFile(file);
                        setCedulaDetras(processed);
                      }} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>
                </div>
                <div className="form-text mb-2">Debe adjuntar ambas caras de la cédula (PDF, JPG o PNG).</div>

                {tipoAfiliacion === 'juridica' && (
                  <>
                    <div className="auth-section-title">Personería jurídica *</div>
                    <input type="file" className={`form-control${required.mark(personeria == null)}`} id="personeria" accept=".pdf,.jpg,.jpeg,.png"
                      aria-label="Personería jurídica"
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        const MAX_FILE_SIZE = 10 * 1024 * 1024;

                        if (!file) return;

                        if (file.size > MAX_FILE_SIZE) {
                          setErrorMessage("El archivo no puede superar los 10 MB.");
                          e.target.value = ""; // limpia el input
                          return;
                        }

                        const processed = await processFile(file);
                        setPersoneria(processed);
                      }} />
                    <div className="invalid-feedback">Campo requerido</div>
                    <div className="form-text mb-2">Máximo 10 MB.</div>
                  </>
                )}

                <div className="auth-section-title">Fotografías del local comercial *</div>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <label htmlFor="foto1" className="form-label small text-muted mb-1">Foto 1 (requerida) *</label>
                    <input type="file" className={`form-control${required.mark(fotosLocal[0] == null)}`} id="foto1" accept=".jpg,.jpeg,.png,.pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        const processed = await processFile(file);
                        handleFotoChange(0, processed);
                      }} />
                    <div className="invalid-feedback">Campo requerido</div>
                  </div>

                  {Array.from({ length: fotosLocalCount - 1 }).map((_, index) => (
                    <div key={index + 2}>
                      <label htmlFor={`foto${index + 2}`} className="form-label small text-muted mb-1">
                        Foto {index + 2} (opcional)
                      </label>
                      <input type="file" className="form-control" id={`foto${index + 2}`} accept=".jpg,.jpeg,.png,.pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0] || null;
                          const processed = await processFile(file);
                          handleFotoChange(index + 1, processed);
                        }} />
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                  <span className="form-text m-0">Puede adjuntar hasta 5 fotografías en total.</span>
                  {fotosLocalCount < 5 && (
                    <button type="button" className="btn btn-outline-primary btn-sm"
                      onClick={() => setFotosLocalCount(fotosLocalCount + 1)}>
                      <i className="bi bi-plus-lg me-1" /> Agregar otra foto
                    </button>
                  )}
                </div>

                <div className="auth-section-title">Para finalizar</div>
                <label htmlFor="referralSource" className="form-label small text-muted mb-1">¿Cómo nos conoció?</label>
                <input type="text" className="form-control" id="referralSource" placeholder="Redes sociales, recomendación, etc."
                  value={referralSource} onChange={(e) => setReferralSource(e.target.value)} />

                <div className="auth-box mt-3">
                  <div className="form-check mb-2">
                    <input type="checkbox" className={`form-check-input${required.mark(!acceptedTerms)}`} id="acceptedTerms"
                      checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} />
                    <label className="form-check-label small" htmlFor="acceptedTerms">
                      Acepto los{" "}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                        Términos y Condiciones de SwiftPay
                      </a> *
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <input type="checkbox" className={`form-check-input${required.mark(!acceptedPrivacy)}`} id="acceptedPrivacy"
                      checked={acceptedPrivacy} onChange={() => setAcceptedPrivacy(!acceptedPrivacy)} />
                    <label className="form-check-label small" htmlFor="acceptedPrivacy">
                      Acepto la{" "}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                        Política de Privacidad de SwiftPay
                      </a> *
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* NAVEGACIÓN */}
            <div className="d-flex flex-column-reverse flex-sm-row justify-content-between gap-2 mt-4 pt-3 border-top">
              {step === 1 ? (
                <button type="button" className="btn btn-outline-secondary" onClick={toLogin}>
                  <i className="bi bi-arrow-left me-1" /> Volver
                </button>
              ) : (
                <button type="button" className="btn btn-outline-secondary" onClick={prevStep}>
                  <i className="bi bi-arrow-left me-1" /> Atrás
                </button>
              )}

              {step === 1 && (
                <button type="submit" className="btn btn-primary px-4">
                  Iniciar afiliación <i className="bi bi-arrow-right ms-1" />
                </button>
              )}
              {step > 1 && step < 5 && (
                <button type="submit" className="btn btn-primary px-4">
                  Siguiente <i className="bi bi-arrow-right ms-1" />
                </button>
              )}
              {step === 5 && (
                <button type="submit" className="btn btn-primary px-4">
                  <i className="bi bi-check-lg me-1" /> Finalizar afiliación
                </button>
              )}
            </div>
          </form>

        </div>
      </main>

      <footer className="auth-footer">© {new Date().getFullYear()} {BRAND_NAME}</footer>

      {errorMessage.length > 0 && (
        <Message type='error' message={errorMessage} closeDialog={removeError} handleOk={removeError} backgroundColor='rgba(0,0,0,0.4)' />
      )}

      {successMessage.length > 0 && (
        <Message type='success' message={successMessage} closeDialog={removeSuccessMessage} handleOk={removeSuccessMessage} backgroundColor='rgba(0,0,0,0.4)' />
      )}
    </div>
  );
};

export default RegisterPage;
